import PropTypes from 'prop-types';
import React, {useState, useRef, useEffect} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import _ from 'lodash';

import {Orientation} from '../../../constants/dataEnum';

import Labware, {WELL_HOVER_MODE} from '../../../components/Labware';

import {getWellStyleColorByLabwareWellCount} from '../../../stores/business/labwareBusinessAction';

import {useClickOutside} from '../../../lib/customHooks';
import {mergeRefs} from '../../../lib/refHelper';

import './CustomTips.scss';

const hoverWellColorEnum = '#C0D6FB';
const selectedWellColorEnum = '#FFD7AE';

const mapDispatchToProps = (dispatch) => ({
  labwareBusinessGetWellStyleColorByLabwareWellCount: bindActionCreators(getWellStyleColorByLabwareWellCount, dispatch),
});

function CustomTips({
  open,
  initialTipCount,
  onTipsSelected,
  onClosed,
  labwareBusinessGetWellStyleColorByLabwareWellCount,
  excludedComponentRef,
  top,
  customTipRef,
}) {
  const [selectedWellStyle, setSelectedWellStyle] = useState({});

  const tipCountRef = useRef(null);
  const swipeStartingWellRef = useRef(null);
  const containerRef = useRef(null);
  const hoveredWellRef = useRef(null);

  const updateWellStyle = () => {
    const isMoving = swipeStartingWellRef.current;

    let selectedWellMap = {};
    let hoveredWellMap = {};

    if (tipCountRef.current) {
      const {startingRow, startingColumn, row: rowTipCount, column: columnTipCount} = tipCountRef.current;

      selectedWellMap = labwareBusinessGetWellStyleColorByLabwareWellCount(
        8,
        12,
        startingRow,
        startingColumn,
        selectedWellColorEnum,
        rowTipCount,
        columnTipCount,
        null,
        false,
        null,
        null,
        0,
        0,
      );
    }

    if (hoveredWellRef.current && !isMoving) {
      const {row: hoveredRow, column: hoveredColumn} = hoveredWellRef.current;

      hoveredWellMap = labwareBusinessGetWellStyleColorByLabwareWellCount(
        8,
        12,
        hoveredRow,
        hoveredColumn,
        hoverWellColorEnum,
        1,
        1,
        null,
        false,
        null,
        null,
        0,
        0,
      );

      Object.keys(hoveredWellMap).forEach((e) => {
        selectedWellMap[e] = hoveredWellMap[e];
      });
    }

    setSelectedWellStyle(selectedWellMap);
  };

  useEffect(() => {
    if (!initialTipCount) {
      tipCountRef.current = null;
      hoveredWellRef.current = null;

      updateWellStyle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useClickOutside(
    containerRef,
    () => {
      onClosed();
    },
    excludedComponentRef,
  );

  const handleSwipeStart = (wellID) => {
    const [row, column] = wellID.split('-');

    swipeStartingWellRef.current = {row: parseInt(row, 10), column: parseInt(column, 10)};
  };

  const handleSwipeEnd = () => {
    if (!swipeStartingWellRef.current) {
      return;
    }

    swipeStartingWellRef.current = null;

    const tipCount = tipCountRef?.current;

    if (!tipCount || !tipCount.row || !tipCount.column) {
      return;
    }

    onTipsSelected(tipCount);
  };

  const handleSwipeMove = (wellID) => {
    if (!swipeStartingWellRef?.current) {
      return;
    }

    const [row, column] = wellID.split('-');

    const startingRow = Math.min(swipeStartingWellRef.current.row, row);
    const startingColumn = Math.min(swipeStartingWellRef.current.column, column);

    const rowTipCount = Math.abs(swipeStartingWellRef.current.row - row) + 1;
    const columnTipCount = Math.abs(swipeStartingWellRef.current.column - column) + 1;

    tipCountRef.current = {startingRow, startingColumn, row: rowTipCount, column: columnTipCount};

    updateWellStyle();
  };

  const handleHoverWell = (wellID) => {
    const [row, column] = wellID.split('-');

    hoveredWellRef.current = {row, column};

    updateWellStyle();
  };

  const handleLeaveWell = () => {
    hoveredWellRef.current = null;

    updateWellStyle();
  };

  const handleClickWell = (wellID) => {
    handleSwipeStart(wellID);

    setTimeout(() => {
      handleSwipeMove(wellID);

      setTimeout(() => {
        handleSwipeEnd();
      }, 1);
    }, 1);
  };

  return (
    <div
      className="custom-tips"
      style={{visibility: open ? 'unset' : 'hidden', top}}
      ref={mergeRefs(containerRef, customTipRef)}
    >
      <div className="custom-tips-info">
        <span>Select a custom pipetting channel</span>
      </div>
      <div style={{height: 268, padding: 15}}>
        <Labware
          key="96-custom-tips-labware"
          ID="96-custom-tips-labware"
          colCount={12}
          rowCount={8}
          orientation={Orientation.POTRAIT}
          isWellRectangle
          swipeEnable
          showSelectionLine={false}
          wellStyle={selectedWellStyle}
          onHoverWell={handleHoverWell}
          onLeaveWell={handleLeaveWell}
          onClickWell={handleClickWell}
          onSwipeStart={handleSwipeStart}
          onSwipeEnd={handleSwipeEnd}
          onSwipeMove={handleSwipeMove}
          isCustomTips
          wellHoverMode={WELL_HOVER_MODE.NOTHING}
        />
      </div>
      {tipCountRef && tipCountRef.current && tipCountRef.current.row && tipCountRef.current.column && (
        <div className="custom-tips-tip-count">{`${tipCountRef.current.row}x${tipCountRef.current.column}`}</div>
      )}
    </div>
  );
}

CustomTips.propTypes = {
  labwareBusinessGetWellStyleColorByLabwareWellCount: PropTypes.func.isRequired,
  initialTipCount: PropTypes.object,
  open: PropTypes.bool,
  onTipsSelected: PropTypes.func,
  onClosed: PropTypes.func,
  excludedComponentRef: PropTypes.any,
  customTipRef: PropTypes.any,
  top: PropTypes.number,
};

CustomTips.defaultProps = {
  open: false,
  initialTipCount: null,
  onTipsSelected: () => {},
  onClosed: () => {},
  excludedComponentRef: null,
  customTipRef: null,
  top: null,
};

const areEqual = (prevProps, nextProps) =>
  _.isEqual(prevProps.initialTipCount, nextProps.initialTipCount) &&
  prevProps.open === nextProps.open &&
  prevProps.top === nextProps.top;

export default React.memo(connect(null, mapDispatchToProps)(CustomTips), areEqual);
